import React from "react";
import "./WaxWallet.scss";

import myCloudWalletSignup from "assets/images/waxWallet/mycloudwallet-signup.png";
import waxWalletActivationFree from "assets/images/waxWallet/wax-wallet-activation-fee-screen.png";
import miutGatianConceptArt from "assets/images/waxWallet/miut-gatian-concept-art.jpeg";
import { TextPageLayout } from "shared/components/TextPageLayout";

export const WaxWallet: React.FC = React.memo(() => {
  return (
    <TextPageLayout title="Free WAX Wallet" className="wax-wallet-layout">
      <article className="wax-wallet">
        <header className="wax-wallet-header">
          <h1 className="wax-wallet-title">
            How to Create a WAX Cloud Wallet in 2026 (And Bypass the 5 WAX
            Activation Fee)
          </h1>
        </header>

        <section className="wax-wallet-section summary-block">
          <p className="wax-wallet-paragraph">
            <strong>TL;DR:</strong> The default WAX MyCloudWallet requires a 5
            WAX fee sent to <code className="wax-wallet-code">create.wax</code>{" "}
            for activation. You can bypass the 5 WAX activation fee for free by
            registering for <strong>Neuroborn Cats</strong> and submitting your
            wallet memo code in the community Discord. The developers will fund
            the activation transaction on your behalf.
          </p>
        </section>

        <section className="wax-wallet-section">
          <p className="wax-wallet-paragraph">
            If you are exploring the WAX blockchain in 2026, setting up a wallet
            is your first mandatory step. However, what starts as a simple
            registration process quickly turns into a frustrating roadblock for
            many beginners: the dreaded activation fee.
          </p>
          <p className="wax-wallet-paragraph">
            In this guide, we will walk you through the exact steps to create
            your account and show you exactly{" "}
            <strong>how to bypass the 5 WAX activation fee entirely</strong> so
            you can start exploring without buying crypto upfront.
          </p>
        </section>

        <section className="wax-wallet-section">
          <h2 className="wax-wallet-subtitle">
            Anchor vs. MyCloudWallet: Which Should You Choose?
          </h2>
          <p className="wax-wallet-paragraph">
            Currently, there are two main wallets actively used on the WAX
            blockchain: <strong>Anchor</strong> and{" "}
            <strong>MyCloudWallet</strong> (formerly CloudWallet).
          </p>
          <p className="wax-wallet-paragraph">
            If you are a beginner,{" "}
            <strong>MyCloudWallet is the default choice.</strong> Anchor is a
            fantastic non-custodial wallet for experienced power users, but its
            technical setup is often overwhelming for newcomers. MyCloudWallet
            is custodial, actively promoted by the blockchain, and integrates
            seamlessly with almost every decentralized app (dApp) and game on
            the network.
          </p>
        </section>

        <section className="wax-wallet-section">
          <h2 className="wax-wallet-subtitle">
            Step-by-Step Guide to Registering Your WAX Wallet
          </h2>
          <p className="wax-wallet-paragraph">
            Starting the registration process is straightforward. Here is what
            you need to do:
          </p>
          <ol className="wax-wallet-list">
            <li className="wax-wallet-list-item">
              <strong>Visit the official portal:</strong> Go to the official
              MyCloudWallet website and click "Sign Up."
            </li>
            <li className="wax-wallet-list-item">
              <strong>Enter your details:</strong> Provide a valid email address
              and create a strong, secure password.
            </li>
            <li className="wax-wallet-list-item">
              <strong>Verify your email:</strong> Check your inbox for the
              confirmation code and enter it on the registration screen.
            </li>
            <li className="wax-wallet-list-item">
              <strong>Secure your seed phrase:</strong> You will be given a
              12-word recovery phrase.{" "}
              <strong>Write this down on physical paper</strong> and store it
              safely. If you lose your password, this is the only way to recover
              your account.
            </li>
          </ol>

          <figure className="wax-wallet-figure">
            <img
              className="wax-wallet-image"
              src={myCloudWalletSignup}
              alt="The official MyCloudWallet account creation screen where users enter their email and password to start the WAX wallet registration process."
              width="100%"
            />
            <figcaption className="wax-wallet-caption">
              The standard MyCloudWallet registration screen.
            </figcaption>
          </figure>
        </section>

        <section className="wax-wallet-section">
          <h2 className="wax-wallet-subtitle">
            The Catch-22: How to Pay 5 WAX to create.wax
          </h2>
          <p className="wax-wallet-paragraph">
            After safely storing your seed phrase, you will hit the final
            screen. To finalize the creation of your account, the system
            requires you to{" "}
            <strong>
              send 5 WAX to the address{" "}
              <code className="wax-wallet-code">create.wax</code>
            </strong>
            , including a unique activation code in the transaction memo.
          </p>

          <figure className="wax-wallet-figure">
            <img
              className="wax-wallet-image"
              src={waxWalletActivationFree}
              alt="The final WAX Cloud Wallet activation screen displaying the mandatory 5 WAX fee sent to create.wax, with the unique transaction memo code highlighted."
              width="100%"
            />
            <figcaption className="wax-wallet-caption">
              The screen where most users get stuck: the 5 WAX activation fee
              requirement.
            </figcaption>
          </figure>

          <p className="wax-wallet-paragraph">
            This is where most new users get stuck.
          </p>
          <p className="wax-wallet-paragraph">
            The major flaw in this system is that{" "}
            <strong>
              you must send these 5 WAX from an already active wallet.
            </strong>{" "}
            If you do not have another crypto wallet, and you do not have a
            friend already on the WAX blockchain who can pay it for you, you are
            essentially locked out. You cannot activate a WAX wallet without
            crypto, but you need the wallet to hold the crypto.
          </p>
        </section>

        <section className="wax-wallet-section">
          <h2 className="wax-wallet-subtitle">
            How to Get Your Free WAX Wallet Activation
          </h2>
          <p className="wax-wallet-paragraph">
            We know this onboarding process is incredibly frustrating. As the
            developers behind <strong>Neuroborn Cats</strong>—a browser-based
            MMORPG set in a unique alternative cat-cyberpunk universe—we believe
            entering the WAX ecosystem should be seamless.
          </p>
          <p className="wax-wallet-paragraph">
            To help new users overcome this hurdle,{" "}
            <strong>
              Neuroborn Cats provides a free WAX wallet activation service.
            </strong>{" "}
            We will pay the 5 WAX activation fee for you so you can step right
            into our world.
          </p>

          <figure className="wax-wallet-figure">
            <img
              className="wax-wallet-image"
              src={miutGatianConceptArt}
              alt="Concept art of a neon-lit street in the cyberpunk megacity of Miut, featuring an anthropomorphic feline Gatian character from the MMORPG."
              width="100%"
            />
            <figcaption className="wax-wallet-caption">
              Join our cyberpunk MMORPG, and we will cover your 5 WAX wallet
              activation fee!
            </figcaption>
          </figure>

          <p className="wax-wallet-paragraph">
            Here is how to get your wallet activated for free:
          </p>
          <ol className="wax-wallet-list">
            <li className="wax-wallet-list-item">
              <strong>Register for the game:</strong> Create a quick account on
              our official portal at{" "}
              <a
                className="wax-wallet-link"
                href="https://game.neuroborncats.com/"
              >
                game.neuroborncats.com
              </a>
              .
            </li>
            <li className="wax-wallet-list-item">
              <strong>Join our community:</strong> Hop into our{" "}
              <a
                className="wax-wallet-link"
                href="https://discord.com/invite/MgV32NTpAH"
              >
                official Discord server
              </a>
              .
            </li>
            <li className="wax-wallet-list-item">
              <strong>Drop your code:</strong> Navigate to the dedicated{" "}
              <code className="wax-wallet-code">#WAX-wallet-activation</code>{" "}
              channel. Post your Gatian in-game nickname along with the unique
              memo code the WAX registration screen gave you.
            </li>
            <li className="wax-wallet-list-item">
              <strong>Wait for activation:</strong> Our team will send the 5 WAX
              from our server wallet to{" "}
              <code className="wax-wallet-code">create.wax</code> using your
              memo. Within a few minutes, your new MyCloudWallet will be fully
              active!
            </li>
          </ol>
        </section>
        <hr className="wax-wallet-divider" />
        <section className="wax-wallet-section faq-section">
          <h2 className="wax-wallet-subtitle">Frequently Asked Questions</h2>

          <h3 className="wax-wallet-faq-title">
            How do I bypass the 5 WAX activation fee for MyCloudWallet?
          </h3>
          <p className="wax-wallet-paragraph">
            You can bypass the 5 WAX activation fee by joining the official
            Discord server for <strong>Neuroborn Cats</strong>. The developers
            provide a free service where they will pay the 5 WAX fee to the{" "}
            <code className="wax-wallet-code">create.wax</code> address on your
            behalf if you provide your new wallet memo code and in-game
            nickname.
          </p>

          <h3 className="wax-wallet-faq-title">
            Can I activate a WAX Cloud Wallet without buying crypto?
          </h3>
          <p className="wax-wallet-paragraph">
            Yes. While the official process requires 5 WAX, you can use
            community services like the one provided by{" "}
            <strong>Neuroborn Cats</strong> to have the activation fee paid for
            you without needing to purchase cryptocurrency on an exchange.
          </p>

          <h3 className="wax-wallet-faq-title">
            What does "send 5 WAX to create.wax" mean?
          </h3>
          <p className="wax-wallet-paragraph">
            It is the final security and anti-spam measure for creating a new
            custodial WAX wallet. It requires another active account on the
            blockchain to send 5 tokens to a central smart contract (
            <code className="wax-wallet-code">create.wax</code>) with your
            specific memo code to mint your new account.
          </p>
        </section>
      </article>
    </TextPageLayout>
  );
});
